import React, { useState } from 'react';
import kebabCase from 'lodash/kebabCase';
import cn from 'classnames';
import { IBaseElementProps } from 'tbk-components/src/components/BasicElement';
import Image, { IImage } from 'tbk-components/src/components/Image';
import Modal from 'tbk-components/src/components/Modal';
import { ITeamMemberCategoryTax } from '../TeamListing';
import HubSpotEmbedForm, { IHubSpotEmbedFormProps } from '../HubSpotEmbedForm';

/**
 * @usePostData
 * @post_type team_member
 * @forceLinkedData
 */
export interface IProfileCardProps extends IBaseElementProps {
	/**
	 * @noUI
	 */
	ID?: number;
	/**
	 * @noUI
	 */
	category?: ITeamMemberCategoryTax[];
	fullName?: string;
	profileImage: IImage;
	position?: string;
	company?: string;
	/**
	 * @control richtext
	 */
	biography?: string;
	meetingRequestButtonText?: string;
	theme: 'Corporate' | 'Defence';
	hubSpotForm?: IHubSpotEmbedFormProps;
	hasModal?: boolean;
	children?: any;
}

/**
 * Profile Card
 * @icon businessperson
 */
const ProfileCard: React.FC<IProfileCardProps> = ({
	className,
	classNames = [],
	ID,
	category,
	profileImage,
	fullName,
	position,
	company,
	biography,
	meetingRequestButtonText,
	theme = 'Corporate',
	hubSpotForm,
	hasModal = true,
}) => {
	const [showModal, setShowModal] = useState<boolean>(false);
	const [showForm, setShowForm] = useState<boolean>(false);

	// Please excuse the function name :)
	const createToggleFormHandler = (showForm: boolean) => {
		return (e: any) => {
			e.preventDefault();
			e.stopPropagation();
			setShowForm(showForm);
		};
	};

	return (
		<div
			className={
				className ||
				cn(
					`calian-profile-card card border-0 rounded-5 ${
						hubSpotForm?.hubSpotEmbedCode ? '' : 'no-form'
					}`,
					{
						[`theme-${kebabCase(theme)}`]: theme,
					},
					...classNames,
				)
			}
		>
			{!hasModal && (
				<div className="calian-profile-card__modal-link text-start text-decoration-none p-5 p-lg-7">
					{profileImage && (
						<Image
							className="card-img-top w-160px h-160px object-fit-cover rounded-5"
							{...profileImage}
						/>
					)}
					<div className="card-body p-0">
						{fullName && (
							<h4 className="card-title mb-2">{fullName}</h4>
						)}
						{position && (
							<p className="card-text fs-15px fw-normal lh-base mb-4 pb-1">
								{position}
							</p>
						)}
					</div>
				</div>
			)}
			{hasModal && (
				<button
					className="calian-profile-card__modal-link text-start text-decoration-none p-5 p-lg-7"
					onClick={() => setShowModal(true)}
				>
					{profileImage && (
						<Image
							className="card-img-top w-160px h-160px object-fit-cover rounded-5"
							{...profileImage}
						/>
					)}
					<div className="card-body p-0">
						{fullName && (
							<h4 className="card-title mb-2">{fullName}</h4>
						)}
						{position && (
							<p className="card-text fs-15px fw-normal lh-base mb-4 pb-1">
								{position}
							</p>
						)}
						<span className="link-primary text-decoration-none d-inline-block pt-1">
							<i className="icon icon-arrow-right fs-5"></i>
						</span>
					</div>
				</button>
			)}
			{
				// clicking in the back end and having the Modal be rendered puts the page into an unclickalbe state.
				showModal && !document.body.classList.contains('wp-admin') && (
					<Modal
						onCancel={function () {
							setShowModal(false);
							setShowForm(false);
						}}
						modalClassNames={[
							'calian-profile-card__modal',
							'modal-dialog',
							'modal-dialog-centered',
							'modal-xl',
							`theme-${kebabCase(theme)}`,
							`${hubSpotForm?.hubSpotEmbedCode ? '' : 'no-form'}`,
							...(showForm ? ['form-visible'] : []),
						]}
						headerClassNames={[
							'border-0 position-absolute top-0 end-0 z-3',
						]}
						bodyClassNames={[
							'modal-body p-0',
							...(showForm ? ['form-visible'] : []),
						]}
						trapFocus
					>
						<div className="container-fluid">
							<div className="row bg-light-bg position-relative">
								<div className="calian-profile-card__modal--left col ps-0 overflow-hidden position-relative">
									{showForm && (
										<button
											onClick={createToggleFormHandler(
												false,
											)}
											className="close-form-overlay position-absolute w-100 z-1 h-100 top-0 start-0"
										></button>
									)}
									<div className="calian-profile-card__modal--content-container bg-light-bg px-3 py-3 pt-5 pt-sm-3 px-md-5 px-md-5 py-lg-9 ps-lg-8 pb-lg-7">
										<div className="d-flex flex-row align-items-start mb-5 pb-1">
											{profileImage && (
												<div className="calian-profile-card__modal--image-container me-3 me-sm-5">
													<Image
														className="w-160px h-160px object-fit-cover rounded-5"
														{...profileImage}
													/>
												</div>
											)}
											{(fullName ||
												position ||
												company) && (
												<div className="calian-profile-card__modal--heading-container mt-0 mt-sm-3">
													{fullName && (
														<h2 className="mb-0">
															{fullName}
														</h2>
													)}
													{position && (
														<p className="fs-18px fw-normal lh-base mb-2">
															{position}
														</p>
													)}
													{company && (
														<p className="fs-7 mb-0">
															{company}
														</p>
													)}
												</div>
											)}
										</div>
										{biography && (
											<div className="calian-profile-card__modal--biography-container pb-2">
												<div
													className="calian-profile-card__modal--biography fw-light"
													dangerouslySetInnerHTML={{
														__html: biography,
													}}
												/>
											</div>
										)}
										{meetingRequestButtonText &&
											hubSpotForm?.hubSpotEmbedCode && (
												<button
													className="d-block d-sm-inline-block btn btn-primary"
													onClick={createToggleFormHandler(
														true,
													)}
												>
													{meetingRequestButtonText}
												</button>
											)}
									</div>
								</div>
								{hubSpotForm?.hubSpotEmbedCode && (
									<div className="calian-profile-card__modal--right col p-0 position-absolute">
										{!showForm && (
											<button
												onClick={createToggleFormHandler(
													true,
												)}
												className="open-form-overlay position-absolute w-100 z-1 h-100 top-0 start-0"
											></button>
										)}
										<div className="calian-profile-card__modal--form-container z-0 position-relative">
											<div className="calian-profile-card__modal--form">
												<HubSpotEmbedForm
													{...hubSpotForm}
												/>
											</div>
										</div>
									</div>
								)}
							</div>
						</div>
					</Modal>
				)
			}
		</div>
	);
};
export default ProfileCard;
